/**
 * Service Worker update detection and management.
 * Monitors for available updates and handles controller changes.
 */

'use strict';

import type { DotNetObjectReference } from '../types.js';
import { safeInvokeSync } from '../utils/dotnet.js';

/**
 * Internal state for service worker functionality.
 */
interface ServiceWorkerState {
    registration: ServiceWorkerRegistration | null;
    dotnet: DotNetObjectReference | null;
    controllerChangeHandler: (() => void) | null;
    updateFoundHandler: (() => void) | null;
}

const state: ServiceWorkerState = {
    registration: null,
    dotnet: null,
    controllerChangeHandler: null,
    updateFoundHandler: null
};

/**
 * Initialize service worker update watcher and notify when an update is available.
 * 
 * @param dotnetHelper - .NET object reference for callbacks
 * @returns Promise resolving to true if service worker is registered
 */
export async function initializeUpdateWatcher(
    dotnetHelper: DotNetObjectReference
): Promise<boolean> {
    try {
        // Detach previous listeners if any
        cleanupListeners();

        state.dotnet = dotnetHelper;
        const registration = await navigator.serviceWorker?.getRegistration?.();
        state.registration = registration ?? null;

        // Notify if an update is already waiting
        if (registration?.waiting) {
            safeInvokeSync(dotnetHelper, 'OnServiceWorkerUpdateAvailable');
        }

        // Set up update detection
        if (registration) {
            state.updateFoundHandler = () => {
                const worker = registration.installing || registration.waiting;
                if (!worker) {
                    return;
                }

                worker.addEventListener('statechange', () => {
                    if (worker.state === 'installed' && navigator.serviceWorker.controller) {
                        safeInvokeSync(dotnetHelper, 'OnServiceWorkerUpdateAvailable');
                    }
                });
            };
            registration.addEventListener('updatefound', state.updateFoundHandler);
        }

        // Optional: reload after activation when using skipWaiting
        // This is a placeholder for applications that use skipWaiting() flow
        state.controllerChangeHandler = () => {
            // No-op: place to reload if using skipWaiting flow
        };
        navigator.serviceWorker?.addEventListener?.('controllerchange', state.controllerChangeHandler);

        return !!registration;
    } catch {
        return false;
    }
}

/**
 * Clean up service worker event listeners.
 */
function cleanupListeners(): void {
    if (state.registration && state.updateFoundHandler) {
        try {
            state.registration.removeEventListener('updatefound', state.updateFoundHandler);
        } catch {
            // Ignore errors during cleanup
        }
    }

    if (state.controllerChangeHandler) {
        try {
            navigator.serviceWorker?.removeEventListener?.(
                'controllerchange',
                state.controllerChangeHandler
            );
        } catch {
            // Ignore errors during cleanup
        }
    }
}

/**
 * Dispose service worker listeners and clear state.
 */
export function dispose(): void {
    try {
        cleanupListeners();
    } finally {
        state.updateFoundHandler = null;
        state.controllerChangeHandler = null;
        state.dotnet = null;
        state.registration = null;
    }
}

/**
 * Reload the page (useful after a service worker update).
 */
export function reload(): void {
    try {
        location.reload();
    } catch {
        // Ignore errors if reload fails
    }
}
